import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Trash2, Ban, Check, AlertTriangle, Clock } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import TimeoutDialog from "@/components/admin/TimeoutDialog";

export default function ReportActions({ report, busy, onDismiss, onDelete, onBan, onEscalated }) {
  const { user } = useAuth();
  const isMod = user?.role === "moderator";
  const [escalateOpen, setEscalateOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [pending, setPending] = useState(false);
  const [timeoutOpen, setTimeoutOpen] = useState(false);

  const escalate = async () => {
    setPending(true);
    try {
      await api.post(`/admin/reports/${report.id}/escalate`, { reason: reason.trim() || "Needs admin review" });
      toast.success("Escalated to administrators");
      setEscalateOpen(false);
      setReason("");
      onEscalated?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setPending(false);
  };

  return (
    <div className="mt-4 flex flex-wrap gap-2">
      <Button size="sm" variant="outline" disabled={busy} onClick={onDismiss} data-testid={`btn-dismiss-report-${report.id}`} className="rounded-full border-slate-200 text-slate-600 hover:bg-slate-50"><Check className="mr-1.5 h-4 w-4" />Dismiss</Button>
      <Button size="sm" variant="outline" disabled={busy || report.message_deleted} onClick={onDelete} data-testid={`btn-delete-message-${report.id}`} className="rounded-full border-slate-200 text-slate-700 hover:bg-slate-50"><Trash2 className="mr-1.5 h-4 w-4" />Delete message</Button>
      {isMod && <Button size="sm" variant="outline" disabled={busy} onClick={() => setEscalateOpen(true)} data-testid={`btn-escalate-report-${report.id}`} className="rounded-full border-amber-200 text-amber-700 hover:bg-amber-50"><AlertTriangle className="mr-1.5 h-4 w-4" />Report to Admin</Button>}
      {isMod && <Button size="sm" variant="outline" disabled={busy} onClick={() => setTimeoutOpen(true)} data-testid={`btn-timeout-user-${report.id}`} className="rounded-full border-purple-200 text-purple-700 hover:bg-purple-50"><Clock className="mr-1.5 h-4 w-4" />Timeout @{report.reported_username}</Button>}
      {!isMod && <Button size="sm" disabled={busy || report.reported_user_banned} onClick={onBan} data-testid={`btn-ban-user-${report.id}`} className="rounded-full bg-red-500 text-white hover:bg-red-600"><Ban className="mr-1.5 h-4 w-4" />Ban @{report.reported_username}</Button>}
      <TimeoutDialog open={timeoutOpen} onOpenChange={setTimeoutOpen} username={report.reported_username} reportId={report.id} onDone={onEscalated} />
      <Dialog open={escalateOpen} onOpenChange={setEscalateOpen}>
        <DialogContent className="max-w-md rounded-3xl" data-testid={`escalate-dialog-${report.id}`}>
          <DialogTitle className="font-heading font-extrabold text-slate-900">Escalate to administrators</DialogTitle>
          <DialogDescription>Explain briefly why this needs admin attention. Admins will see this in the High Priority section.</DialogDescription>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} placeholder="e.g., borderline hate speech, unsure about intent…" data-testid={`escalate-reason-${report.id}`} className="min-h-24 rounded-2xl" />
          <DialogFooter><Button variant="outline" onClick={() => setEscalateOpen(false)} className="rounded-full">Cancel</Button><Button onClick={escalate} disabled={pending || reason.trim().length < 3} data-testid={`btn-confirm-escalate-${report.id}`} className="rounded-full bg-amber-500 text-white hover:bg-amber-600">Escalate</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
